import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {
  DEFAULT_NOTIFICATION_TOTALS,
  DEFAULT_NOTIFICATIONS_CHIPS,
  INotificationTotals
} from '../../../notifications/models';
import {Store} from '@ngrx/store';
import {IFeature, IUser, IUserAccess} from '../../../user-access/models';
import {setGlobalModalVisibility} from '../../../user-access/user-store/user.actions';
import {setSelectedStore, setSelectedStoresById} from '../../../user-access/user-stores-store/user-store.actions';
import {SHARED_MULTI_STORE_SELECTION_LIMIT} from '../../../user-access/utils/last-seen-stores.utils';
import {ModalType} from '../../../global-modal-container/models/modal-enums';
import {DomService} from '../../../../shared/shared-services/dom/dom.service';
import {DocumentTitleService} from '../../../../shared/shared-services/document-title/document-title.service';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {setNotificationFilterChips} from '../../../notifications/store/notification.actions';
import {MenuPopupComponent} from '../../../../shared-modules/shared-module/components/menu-popup/menu-popup.component';
import {
  NavigationPageEnum,
  NavigationPageEnumHumanReadable,
  NavigationTabEnum,
} from '../../../user-access/models/navigations.enums';
import {
  BARCODE_SCANNER_PAGES,
  DESKTOP_PAGES,
  MOBILE_PAGES,
  PAGES_SUB
} from '../../../../shared-utilities/models-old/page/pages-const';
import {ISelectOption} from '../../../../shared/shared-models/custom-select/custom-select';
import {IDashboard} from '../../../dashboard/models/dashboard-interfaces';
import {IStore, DISABLED_STORE_SELECTOR_TOOLTIP, isStoreOperationallyDisabled} from '../../../../shared/shared-models/store/store';
import {canSelectStoreOnCurrentPage} from '../../../../shared/guards/guard-utils';
import {FireAuthService} from '../../../../shared-utilities/services-old/fire-auth.service';
import {NavigationEnd, Router} from '@angular/router';
import {Observable, Subject, takeUntil} from 'rxjs';
import {
  selectCurrentNavigation,
  selectSelectedStore,
  selectSelectedStoresIds,
} from '../../../user-access/user-stores-store/user-store.selectors';
import {INavigate} from '../../../user-access/models/navigation';
import {ModalControllerService} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {PopoverControllerService} from '../../../../shared/shared-services/overlay/popover-controller.service';
import {ISharedModalBasic} from '../../../../shared/shared-models/modals/shared-modal-basic';
import {ISharedSelectModalResult} from '../../../../shared/shared-models/modals/shared-select-modal';
import {
  SharedSelectModalComponent,
} from '../../../../shared/shared-components/components/shared-select-modal/shared-select-modal.component';
import {
  SharedModalBasicComponent
} from '../../../../shared/shared-components/components/shared-modal-basic/shared-modal-basic.component';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {getDataTimeStamps, getFromAppQueue, getImageURL} from '../../store/core.actions';
import {FromAppQueueModalComponent} from '../from-app-queue-modal/from-app-queue-modal.component';
import {selectImageURL} from '../../store/core.selectors';
import {UnsavedChangesService} from '../../../settings-new/services/unsaved-changes/unsaved-changes.service';
import {
  presentSharedActionSheet,
} from '../../../../shared/shared-components/components/shared-action-sheet/shared-action-sheet.component';
import {SharedModalUtils} from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {startHardRefreshWithFollowUp} from '../../../../shared/shared-utils/browser/hard-refresh.utils';

/**
 * Modern page header component that works on both desktop and mobile devices.
 */
@Component({
  selector: 'app-page-header',
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
  standalone: false
})
export class PageHeaderComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @Input() isUserLogOutModalOpen = false;
  @Input() title = '';
  @Input() user: IUser;
  @Input() userAccess: IUserAccess;

  @Output() readonly expandSideMenu: EventEmitter<void> = new EventEmitter<void>();

  totalNotifications: INotificationTotals = {...DEFAULT_NOTIFICATION_TOTALS};
  isMobile: boolean;
  imageURL$: Observable<string>;
  imageUrl: string;

  protected readonly iconColourEnum = IconColourEnum;
  protected readonly icons = IconsEnum;
  protected readonly hardRefreshTooltip =
    'Hard refresh — clears app cache and reloads (not a normal page refresh)';
  private isHardRefreshing = false;
  private isQueueModalOpen = false;
  private isUserAccessLoading = true;
  private isUserNotificationsLoading = true;
  private feature: string;
  private currentUrl = '';
  private navigation: INavigate | null = null;
  private lastRouteKey: string | undefined;
  private lastNonSettingsRouteKey: string | undefined;
  private readonly settingsFeatures = new Set<string>(['SettingsNew']);

  private aggregatedData: IDashboard = {
    discount: {
      totalAmountDiscount: 0,
      totalAmountPrice: 0,
      totalInvoice: 0,
      totalPercentage: 0
    },
    stock: {
      itemsCountedToday: 0,
      itemsCountedLast7Days: 0,
      itemsNotCounted180Days: 0,
      negativeStockItems: 0,
      negativeStockValue: 0
    },
    transactions: {
      basketSize: 0,
      creditNotesValue: 0,
      customerCount: 0,
      grossProfit: 0,
      netCost: 0,
      netSales: 0,
      stockTakes: 0
    }
  };

  public selectedStores: string[] = [];
  public dashboardData: Record<string, IDashboard> = {};
  public stores: IStore[] = [];
  private currentSingleSelectedStoreId: string = '';
  private committedMultiStoreIds: string[] = [];
  private lastPersistedSharedIds: string[] = [];
  private userAdjustedSharedSelection = false;
  private readonly sharedStoreSelectionLimit = SHARED_MULTI_STORE_SELECTION_LIMIT;

  constructor(
    private popoverController: PopoverControllerService,
    private modalController: ModalControllerService,
    private readonly documentTitleService: DocumentTitleService,
    private readonly store: Store,
    private readonly fireAuth: FireAuthService,
    private domService: DomService,
    private router: Router,
    private readonly unsavedChangesService: UnsavedChangesService,
    private readonly sharedModalUtils: SharedModalUtils,
  ) {
    this.isMobile = window.innerWidth < 600;
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 600;
    });
  }

  get bannerForUnreadFailedNotifications(): string {
    const total = this.totalNotifications.unreadFailed;
    const pluralS = total > 1 ? 's' : '';
    const pluralA = total === 1 ? 'a' : '';
    return `${total} Unread Notification${pluralS} For ${pluralA} Failed Event${pluralS}`;
  }

  get getFirstLetter(): string {
    return this.user.userName ? this.getUserName.charAt(0).toUpperCase() : 'M';
  }

  get getIsNotificationDataLoading(): boolean {
    return this.isUserAccessLoading || this.isUserNotificationsLoading;
  }

  get getProfilePicture(): string {
    return this.user?.pp ? this.user?.pp : this.getFirstLetter;
  }

  get getUserName(): string {
    return this.user?.userName ? this.user.userName : null;
  }

  get fullScreen(): boolean {
    return this.domService.isFullscreen();
  }

  get getTitle(): string {
    const title = this.title || NavigationPageEnumHumanReadable[this.feature] || '';
    const isStockManager = this.feature === 'StockManager'
      || this.navigation?.currentPage === NavigationPageEnum.StockManager
      || title.startsWith('Stock Manager')
      || title.startsWith('Stock Lookup');
    if (this.isMobile && isStockManager) {
      return 'Stock Lookup';
    }
    return title;
  }

  get currentIcon(): IconsEnum | null {
    const title = this.getTitle;

    if (title === 'Ordering Schedule') {
      return IconsEnum.calendar;
    }

    const allPages = [
      ...DESKTOP_PAGES,
      ...Object.values(PAGES_SUB).flat(),
      ...MOBILE_PAGES,
      ...BARCODE_SCANNER_PAGES,
    ];

    return (
      allPages.find(p => title.endsWith(p.text))?.icon ?? null
    );
  }

  get notificationsTooltip(): string {
    const unread = this.totalNotifications.unread > 0 ? `${this.totalNotifications.unread} unread notifications` : '';
    const failed = this.totalNotifications.failed > 0 ? `${this.totalNotifications.failed} failed items` : '';
    return `You have ${unread}${this.totalNotifications.unread > 0 && this.totalNotifications.failed > 0 ? ', and ' : ''}${failed}.`;
  }

  get notificationsTooltipUnreadAndUnsuccessful(): string {
    return `You have ${this.totalNotifications.unreadFailed} failed items that are unread.`;
  }

  get isHomePage(): boolean {
    return this.feature === 'Home';
  }

  get isDashboardPage(): boolean {
    return this.feature === 'Dashboard';
  }

  get isMultiStoreStockManager(): boolean {
    return !this.isMobile && this.navigation?.currentTab === NavigationTabEnum.StockManagerMultiStore;
  }

  /**
   * Home keeps a local multi-select. Dashboard and multi-store stock manager
   * share one header selection.
   */
  get usesSharedMultiStoreSelection(): boolean {
    return this.isDashboardPage || this.isMultiStoreStockManager;
  }

  get allowsMultipleStoreSelection(): boolean {
    return this.isHomePage || this.usesSharedMultiStoreSelection;
  }

  /**
   * Dashboard and multi-store stock manager share a selection of at most five stores.
   */
  get storeSelectionLimit(): number | undefined {
    return this.usesSharedMultiStoreSelection ? this.sharedStoreSelectionLimit : undefined;
  }

  get hideStoreSelector(): boolean {
    const parts = this.currentUrl.split('/').filter(Boolean);

    const isStockTakeDetail =
      parts.length >= 3 &&
      parts[0] === 'stock-take' &&
      (parts[1] === 'detail' || parts[1] === 'mobile-detail');

    const isDashboardReporting = this.feature === 'DashboardReporting';

    return isStockTakeDetail || this.navigation?.currentTab === 'preview' || isDashboardReporting;
  }

  get selectedStoreName(): string {
    if (this.allowsMultipleStoreSelection) {
      const count = this.selectedStores.length;
      if (count < 1) {
        return 'No stores selected';
      }
      if (count === 1) {
        return this.getStoreById(this.selectedStores[0])?.name ?? '';
      }
      const selectableCount = this.selectableStoreIds().length;
      if (selectableCount > 0 && count === selectableCount) {
        return 'All stores';
      }
      return `${count} stores`;
    }
    const storeId: string = this.currentSingleSelectedStoreId
      || (this.selectedStores.length === 1 ? this.selectedStores[0] : '');
    if (!storeId) {
      return '';
    }
    return this.getStoreById(storeId)?.name ?? '';
  }

  @Input() set currentFeature(feature: IFeature) {
    if (this.isCurrentRouteSettings()) {
      return;
    }
    this.feature = Object.keys(NavigationPageEnum).find(
      k => NavigationPageEnum[k as keyof typeof NavigationPageEnum] === feature.feature
    );
  }

  @Input() set setNotificationTotals(totals: INotificationTotals) {
    this.totalNotifications = totals;
    this.documentTitleService.setUnreadNotificationCount(totals.unread);
  }

  @Input() set setIsUserAccessLoading(isLoading: boolean) {
    this.isUserAccessLoading = isLoading;
  }

  @Input() set setIsUserNotificationsLoading(isLoading: boolean) {
    this.isUserNotificationsLoading = isLoading;
  }

  @Input() set setUserStores(stores: IStore[]) {
    this.stores = stores;
    this.syncHeaderToCommittedStore();
    this.combineStoreData();
  }

  getMobileDisplayStore(): IStore | null {
    if (this.allowsMultipleStoreSelection) {
      if (this.selectedStores.length !== 1) {
        return null;
      }
      return this.getStoreById(this.selectedStores[0]) ?? null;
    }
    const storeId = this.currentSingleSelectedStoreId || this.selectedStores[0];
    if (!storeId) {
      return this.stores[0] ?? null;
    }
    return this.getStoreById(storeId) ?? this.stores[0] ?? null;
  }

  ngOnInit(): void {

    this.getHelpDiagramImage();
    this.currentUrl = this.router.url;
    this.updateFeatureFromRoute(this.router.url);

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl = event.url;
        this.updateFeatureFromRoute(event.url);
      }
    });

    this.store.select(selectSelectedStore).pipe(
      takeUntil(this.destroy$)
    ).subscribe((selectedStore: IStore | undefined) => {
      if (selectedStore?.storeId) {
        this.currentSingleSelectedStoreId = selectedStore.storeId;
        if (!this.allowsMultipleStoreSelection) {
          this.selectedStores = [selectedStore.storeId];
        }
      }
    });

    this.store.select(selectSelectedStoresIds).pipe(
      takeUntil(this.destroy$),
    ).subscribe((storeIds: string[]) => {
      this.committedMultiStoreIds = storeIds ?? [];
      if (this.usesSharedMultiStoreSelection) {
        this.syncHeaderToCommittedStore();
      }
    });

    this.store.select(selectCurrentNavigation).pipe(
      takeUntil(this.destroy$)
    ).subscribe((navigation: INavigate) => {
      const tabChanged = this.navigation?.currentTab !== navigation?.currentTab
        || this.navigation?.currentPage !== navigation?.currentPage;
      this.navigation = navigation;
      if (tabChanged) {
        this.syncHeaderToCommittedStore();
        this.combineStoreData();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private isCurrentRouteSettings(): boolean {
    const urlParts = this.router.url.split('?')[0].split('/').filter((part: string) => part.length > 0);
    const routeFeature = urlParts[0] || 'home';
    const key = Object.keys(NavigationPageEnum).find(
      k => NavigationPageEnum[k as keyof typeof NavigationPageEnum] === routeFeature
    );
    return this.settingsFeatures.has(key);
  }

  private updateFeatureFromRoute(url: string): void {
    const effectivePrevious = this.settingsFeatures.has(this.lastRouteKey)
      ? this.lastNonSettingsRouteKey
      : this.lastRouteKey;

    const urlParts = url.split('?')[0].split('/').filter(part => part.length > 0);
    const routeFeature = urlParts[0] || 'home';
    const key = Object.keys(NavigationPageEnum).find(
      k => NavigationPageEnum[k as keyof typeof NavigationPageEnum] === routeFeature
    );

    if (key) {
      this.feature = key;
      this.lastRouteKey = key;

      if (!this.settingsFeatures.has(key)) {
        this.lastNonSettingsRouteKey = key;
      }
      if (this.stores.length > 0 && effectivePrevious !== key) {
        this.syncHeaderToCommittedStore();
        this.store.dispatch(getDataTimeStamps());
        this.combineStoreData();
      }
    }
  }

  expandMenu(event: Event): void {
    event.stopPropagation();
    this.expandSideMenu.emit();
  }

  async viewNotifications(viewFailedUnread: boolean = false): Promise<void> {
    const filters = {...DEFAULT_NOTIFICATIONS_CHIPS};
    if (viewFailedUnread === true) {
      filters.allMessages = false;
      filters.fail = true;
      filters.readMessages = false;
      filters.success = false;
      filters.unreadMessages = true;
    }
    this.store.dispatch(setNotificationFilterChips({filters}));
    this.store.dispatch(setGlobalModalVisibility({visibility: true, modalType: [ModalType.Notifications]}));
  }

  toggleFullScreen(): Promise<void> {
    return this.domService.toggleFullscreen();
  }

  /**
   * Opens the from_app processing queue for every store on the account.
   */
  async onProcessingQueueClick(): Promise<void> {
    if (this.isQueueModalOpen) {
      return;
    }
    this.isQueueModalOpen = true;
    this.store.dispatch(getFromAppQueue({}));
    const modal = await this.modalController.create({
      component: FromAppQueueModalComponent,
      cssClass: 'from-app-queue-modal',
      backdropDismiss: false,
    });
    await modal.present();
    await modal.onDidDismiss();
    this.isQueueModalOpen = false;
  }

  /**
   * Confirms with the user, then clears service worker / Cache Storage and reloads.
   */
  async onHardRefreshClick(): Promise<void> {
    if (this.isHardRefreshing) {
      return;
    }

    const result = await this.sharedModalUtils.createModal({
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      modalTitle: 'Hard Refresh',
      contextHeading: 'This is not a normal page refresh',
      contextDescription: [
        'A hard refresh clears the app cache and service worker, then reloads Gallix so you get the latest version of the app.',
        'Use this if the app looks stuck on an old version, or if a normal browser refresh did not fix the issue.',
        'Your session stays signed in. Local settings stored in the browser are not cleared.',
      ],
      buttonAcceptText: 'Hard Refresh',
      buttonCloseText: 'Cancel',
      buttonAccept: true,
      buttonClose: true,
      buttonCloseCross: false,
      cssClass: ['shared-basic-modal-css-small'],
    });

    if (result.data?.buttonPressed !== 'button-accept') {
      return;
    }

    this.isHardRefreshing = true;
    await startHardRefreshWithFollowUp();
  }

  async onOpenMenuClick(event?: MouseEvent): Promise<void> {
    const pp: string[] = [
      this.user?.pp ? 'picture' : 'letter',
      this.getProfilePicture
    ];
    const pc = await this.popoverController.create({
      component: MenuPopupComponent,
      componentProps: {user: this.user, pp},
      cssClass: 'menu-popup',
      event,
    });

    await pc.present();
  }

  async onStoreSelectionChange(selected: Record<string, ISelectOption>): Promise<void> {
    this.selectedStores = Object.keys(selected).filter(
      (storeId: string) => {
        const option = selected[storeId];
        const store = this.getStoreById(storeId);
        return option.selected && !option.disabled && !isStoreOperationallyDisabled(store);
      },
    );

    if (this.allowsMultipleStoreSelection) {
      if (this.usesSharedMultiStoreSelection) {
        this.selectedStores = this.limitSharedStoreIds(this.selectedStores);
        this.userAdjustedSharedSelection = true;
        this.persistSharedMultiStoreSelection(this.selectedStores);
      }
      this.combineStoreData();
      return;
    }

    if (this.selectedStores.length !== 1) {
      this.revertHeaderStoreSelection();
      return;
    }

    const selectedStore = this.getStoreById(this.selectedStores[0]);
    if (selectedStore && selectedStore.storeId !== this.currentSingleSelectedStoreId) {
      const canProceed: boolean = await this.unsavedChangesService.checkUnsavedChanges();
      if (!canProceed) {
        this.revertHeaderStoreSelection();
        return;
      }
      if (!this.canSelectStoreForCurrentPage(selectedStore)) {
        this.revertHeaderStoreSelection();
        this.store.dispatch(setSelectedStore({selectedStore}));
        this.combineStoreData();
        return;
      }
      this.store.dispatch(setSelectedStore({selectedStore}));
    }

    this.combineStoreData();
  }

  public getHelpDiagramImage(): void {
    const image = 'gallix_cloud_flow_chart.svg';
    this.store.dispatch(getImageURL({path: `/site_media/help/data_freshness/${image}`, image}));
    this.imageURL$ = this.store.select(selectImageURL(image));
    this.imageURL$.subscribe((image: string): void => {
      this.imageUrl = image;
    });
  }

  async onFreshnessExpClick(): Promise<void> {


    const componentProps: ISharedModalBasic = {
      buttonAccept: false,
      buttonAcceptText: '',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Close',
      modalTitle: 'Data Freshness Indicator Explanation',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: this.iconColourEnum.yellow,
      contextHeading: 'NOTE: Explanation of the purpose and functionality of the coloured indicator dots.',
      contextDescription: [
        `This flow chart illustrates how Gallix interacts with your server and explains the meaning of the data freshness indicators.`,
        ``,
        `Remember, a red indicator doesn't necessarily mean your server is disconnected from Gallix; it just signals that the displayed data is outdated. Gallix remains usable, and any actions will process once the server is online.`,
        ``,
        `If the indicator is red and you need the latest stock data, try refreshing the page to trigger an update. If the indicator remains red for more than two minutes, please reach out to us.`,
        ``,
        `When the server is offline, your actions will queue and automatically process once the connection is restored.`,
      ],
      isImageVisible: true,
      imageUrl: this.imageUrl,
    };

    const modalController = await this.modalController.create({
      component: SharedModalBasicComponent,
      componentProps: {
        ...componentProps,
        popoverReference: this.popoverController,
      },
      cssClass: ['shared-basic-modal-css-larger'],
    });
    await modalController.present();
    await modalController.onDidDismiss();
  }

  async onMobileStoreClick(): Promise<void> {
    if (this.allowsMultipleStoreSelection) {
      await this.onMobileMultiStoreClick();
      return;
    }
    const modal = await this.modalController.create({
      component: SharedSelectModalComponent,
      componentProps: {
        modalTitle: 'Select Store',
        options: this.getStoresAsSingleSelectOptions(),
        buttonAcceptText: 'Select',
        buttonCloseText: 'Cancel',
        showSearchBar: this.stores.length >= 8,
      },
      cssClass: 'shared-select-modal-css',
      backdropDismiss: false,
    });
    await modal.present();
    const {data} = await modal.onDidDismiss();
    const result = data as ISharedSelectModalResult | undefined;
    const storeId = result?.confirmed ? result.selectedKey : undefined;
    if (!storeId) {
      return;
    }

    const selectedStore = this.getStoreById(storeId);
    if (!selectedStore || isStoreOperationallyDisabled(selectedStore)) {
      return;
    }
    if (selectedStore.storeId === this.currentSingleSelectedStoreId) {
      this.combineStoreData();
      return;
    }
    const canProceed: boolean = await this.unsavedChangesService.checkUnsavedChanges();
    if (!canProceed) {
      return;
    }
    if (!this.canSelectStoreForCurrentPage(selectedStore)) {
      this.store.dispatch(setSelectedStore({selectedStore}));
      this.combineStoreData();
      return;
    }
    this.selectedStores = [storeId];
    this.store.dispatch(setSelectedStore({selectedStore}));
    this.combineStoreData();
  }

  async onMobileMenuClick(): Promise<void> {
    const {data} = await presentSharedActionSheet(this.modalController, {
      header: this.getUserName || 'Menu',
      buttons: [
        {id: 'processing-queue', text: 'Processing Queue', icon: this.icons.progressClock},
        {id: 'hard-refresh', text: 'Hard Refresh', icon: this.icons.sync},
        {id: 'notifications', text: 'Notifications', icon: this.icons.bellOutline},
        {id: 'messages', text: 'Messages', icon: this.icons.mail},
        {id: 'freshness', text: 'Data Freshness', icon: this.icons.infoSlabCircleOutline},
        {id: 'profile', text: 'Profile', icon: this.icons.person},
        {id: 'cancel', text: 'Cancel', role: 'cancel'},
      ],
    });
    if (!data?.confirmed || !data.selectedId) {
      return;
    }
    switch (data.selectedId) {
      case 'processing-queue':
        await this.onProcessingQueueClick();
        break;
      case 'hard-refresh':
        await this.onHardRefreshClick();
        break;
      case 'notifications':
        await this.viewNotifications(true);
        break;
      case 'messages':
        await this.viewNotifications();
        break;
      case 'freshness':
        await this.onFreshnessExpClick();
        break;
      case 'profile':
        await this.onOpenMenuClick();
        break;
      default:
        break;
    }
  }

  /**
   * Whether the given store can use the current page. Used so a no-access switch still commits.
   */
  private canSelectStoreForCurrentPage(store: IStore): boolean {
    return canSelectStoreOnCurrentPage(
      this.fireAuth,
      store.storeId,
      this.navigation?.currentPage ?? '',
    );
  }

  /**
   * Opens the mobile store list in multi-select mode.
   */
  private async onMobileMultiStoreClick(): Promise<void> {
    const modal = await this.modalController.create({
      component: SharedSelectModalComponent,
      componentProps: {
        modalTitle: 'Select Stores',
        options: this.getStoresAsISelectOptions(),
        allowMultiple: true,
        allowSelectAll: true,
        buttonAcceptText: 'Apply',
        buttonCloseText: 'Cancel',
        showSearchBar: this.stores.length >= 8,
      },
      cssClass: 'shared-select-modal-css',
      backdropDismiss: false,
    });
    await modal.present();
    const {data} = await modal.onDidDismiss();
    const result = data as ISharedSelectModalResult | undefined;
    if (!result?.confirmed) {
      return;
    }
    const selectedIds = (result.selectedKeys ?? [])
      .filter((storeId: string) => !isStoreOperationallyDisabled(this.getStoreById(storeId)));
    this.selectedStores = this.usesSharedMultiStoreSelection
      ? this.limitSharedStoreIds(selectedIds)
      : selectedIds;
    if (this.usesSharedMultiStoreSelection) {
      this.userAdjustedSharedSelection = true;
      this.persistSharedMultiStoreSelection(this.selectedStores);
    }
    this.combineStoreData();
  }

  /**
   * Writes the shared dashboard / multi-store selection.
   */
  private persistSharedMultiStoreSelection(storeIds: string[]): void {
    if (this.sameStoreIds(storeIds, this.committedMultiStoreIds) || this.sameStoreIds(storeIds, this.lastPersistedSharedIds)) {
      return;
    }
    this.lastPersistedSharedIds = [...storeIds];
    this.committedMultiStoreIds = [...storeIds];
    this.store.dispatch(setSelectedStoresById({selected: storeIds}));
  }

  /**
   * Store ids the header is allowed to select.
   */
  private selectableStoreIds(): string[] {
    return this.stores
      .filter((store: IStore) => !isStoreOperationallyDisabled(store))
      .map((store: IStore) => store.storeId);
  }

  /**
   * True when both lists contain the same store ids.
   */
  private sameStoreIds(left: string[], right: string[]): boolean {
    if (left.length !== right.length) {
      return false;
    }
    const rightIds = new Set(right);
    return left.every((storeId: string) => rightIds.has(storeId));
  }

  /**
   * Keeps a shared selection inside the five-store cap, preserving header order.
   */
  private limitSharedStoreIds(storeIds: string[]): string[] {
    const selected = new Set(storeIds);
    return this.selectableStoreIds()
      .filter((storeId: string) => selected.has(storeId))
      .slice(0, this.sharedStoreSelectionLimit);
  }

  /**
   * Home shows every selectable store as selected. Shared multi pages default to the
   * first five stores. Other pages show only the committed store.
   */
  private syncHeaderToCommittedStore(): void {
    if (this.isHomePage) {
      this.selectedStores = this.selectableStoreIds();
      return;
    }
    if (this.usesSharedMultiStoreSelection) {
      const selectable = this.selectableStoreIds();
      if (this.userAdjustedSharedSelection) {
        this.selectedStores = this.limitSharedStoreIds(this.committedMultiStoreIds);
        return;
      }
      const topStores = selectable.slice(0, this.sharedStoreSelectionLimit);
      this.selectedStores = topStores;
      if (topStores.length > 0) {
        this.persistSharedMultiStoreSelection(topStores);
      }
      return;
    }
    const currentStoreId: string = this.currentSingleSelectedStoreId;
    if (currentStoreId) {
      this.selectedStores = [currentStoreId];
      return;
    }
    const firstSelectableStore: IStore | undefined = this.stores.find(
      (store: IStore) => !isStoreOperationallyDisabled(store),
    );
    this.selectedStores = firstSelectableStore ? [firstSelectableStore.storeId] : [];
  }

  /**
   * Restores the header store selector to the last committed store.
   */
  private revertHeaderStoreSelection(): void {
    this.syncHeaderToCommittedStore();
    this.combineStoreData();
  }

  combineStoreData(): void {
    this.aggregatedData = {
      discount: {
        totalAmountDiscount: 0,
        totalAmountPrice: 0,
        totalInvoice: 0,
        totalPercentage: 0
      },
      stock: {
        itemsCountedToday: 0,
        itemsCountedLast7Days: 0,
        itemsNotCounted180Days: 0,
        negativeStockItems: 0,
        negativeStockValue: 0
      },
      transactions: {
        basketSize: 0,
        creditNotesValue: 0,
        customerCount: 0,
        grossProfit: 0,
        grossProfitPercentage: 0,
        netCost: 0,
        netSales: 0,
        stockTakes: 0
      }
    };

    const storeIdsForAggregation: string[] = this.isHomePage
      ? this.stores
        .filter((store: IStore) => !isStoreOperationallyDisabled(store))
        .map((store: IStore) => store.storeId)
      : this.selectedStores;

    storeIdsForAggregation.forEach((storeId: string) => {
      const data = this.dashboardData[storeId];
      if (data) {
        this.aggregatedData.discount.totalAmountDiscount += data.discount.totalAmountDiscount;
        this.aggregatedData.discount.totalAmountPrice += data.discount.totalAmountPrice;
        this.aggregatedData.discount.totalInvoice += data.discount.totalInvoice;
        this.aggregatedData.discount.totalPercentage += data.discount.totalPercentage;

        this.aggregatedData.stock.itemsNotCounted180Days += data.stock.itemsNotCounted180Days;
        this.aggregatedData.stock.itemsCountedToday += data.stock.itemsCountedToday;
        this.aggregatedData.stock.itemsCountedLast7Days += data.stock.itemsCountedLast7Days;
        this.aggregatedData.stock.negativeStockItems += data.stock.negativeStockItems;
        this.aggregatedData.stock.negativeStockValue += data.stock.negativeStockValue;

        this.aggregatedData.transactions.creditNotesValue += data.transactions.creditNotesValue;
        this.aggregatedData.transactions.customerCount += data.transactions.customerCount;
        this.aggregatedData.transactions.grossProfit += data.transactions.grossProfit;
        this.aggregatedData.transactions.netCost += data.transactions.netCost;
        this.aggregatedData.transactions.netSales += data.transactions.netSales;
        this.aggregatedData.transactions.stockTakes += data.transactions.stockTakes;
      }
    });
    if (this.aggregatedData.transactions.netSales !== 0 || this.aggregatedData.transactions.customerCount !== 0) {
      this.aggregatedData.transactions.basketSize = (this.aggregatedData.transactions.netSales / this.aggregatedData.transactions.customerCount);
    }
    if (this.aggregatedData.transactions.grossProfit !== 0 || this.aggregatedData.transactions.netSales !== 0) {
      this.aggregatedData.transactions.grossProfitPercentage = (this.aggregatedData.transactions.grossProfit / this.aggregatedData.transactions.netSales);
    }
    this.aggregatedData.discount.totalDiscount = (this.aggregatedData.discount.totalAmountDiscount + this.aggregatedData.discount.totalAmountPrice);
    if (this.aggregatedData.discount.totalAmountPrice !== 0 && this.aggregatedData.transactions.netSales !== 0) {
      this.aggregatedData.discount.totalPercentage = (
        this.aggregatedData.discount.totalDiscount / this.aggregatedData.transactions.netSales
      );
    }
  }

  getStoresAsISelectOptions(): Record<string, ISelectOption> {
    const options: Record<string, ISelectOption> = {};
    this.stores.forEach((store: IStore) => {
      const isDisabled: boolean = isStoreOperationallyDisabled(store);
      options[store.storeId] = {
        name: store.name,
        selected: !isDisabled && (
          this.allowsMultipleStoreSelection
            ? this.selectedStores.includes(store.storeId)
            : store.storeId === this.currentSingleSelectedStoreId
        ),
        disabled: isDisabled,
        tooltip: isDisabled ? DISABLED_STORE_SELECTOR_TOOLTIP : undefined,
      };
    });
    return options;
  }

  /**
   * Builds store options for the mobile single-select modal.
   * Only the current store is marked selected so Home's multi-store list cannot preselect the wrong store.
   */
  getStoresAsSingleSelectOptions(): Record<string, ISelectOption> {
    const options: Record<string, ISelectOption> = {};
    this.stores.forEach((store: IStore) => {
      const isDisabled: boolean = isStoreOperationallyDisabled(store);
      options[store.storeId] = {
        name: store.name,
        selected: !isDisabled && store.storeId === this.currentSingleSelectedStoreId,
        disabled: isDisabled,
        tooltip: isDisabled ? DISABLED_STORE_SELECTOR_TOOLTIP : undefined,
      };
    });
    return options;
  }

  getStoresKeyOrder(): string[] {
    return this.stores.map((store: IStore) => store.storeId);
  }

  getStoreById(storeId: string): IStore | undefined {
    return this.stores.find((store: IStore) => store.storeId === storeId);
  }

  getStoresAsRecord(): Record<string, IStore> {
    const storesRecord: Record<string, IStore> = {};
    this.stores.forEach((store: IStore) => {
      storesRecord[store.storeId] = store;
    });
    return storesRecord;
  }
}
